import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {DownloadOutlined, StopOutlined} from "@ant-design/icons";
import {Alert, Button, Card, Popconfirm, Space, Table, Typography, App} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {formatLocalDate} from "./adminDateFormat";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {dateSorter, sortText, textSorter} from "./tableSorters";
import {AuthContext} from "../../Contexts/AuthContext";
import {buildAuthorizedFileFetchOptions} from "../../fileDownloadUrl";
import {humanizeEnumValue} from "../../i18n/labelHelpers";
import {downloadResponseFile} from "../../downloadFile";
import {StatusTag} from "../../Components/ui/StatusTag";
import type {StatusTone} from "../../Components/ui/StatusTag";
import {PageHeader} from "../../Components/ui";
import {
    EventRoomingListQuery,
    useEventRoomingListQuery,
    useReleaseEventRoomReservationMutation
} from "../../GraphQL/generated/graphql";

const PAGE_SIZE = 50;

type EventRoomingRegistration = NonNullable<NonNullable<EventRoomingListQuery["eventRegistrations"]>["items"]>[number];

const normalizeEnumKey = (value?: string | null) =>
    (value ?? "").replace(/[^a-z0-9]/gi, "").toLowerCase();

const tagTone = (value: string | null | undefined, tones: Record<string, StatusTone>): StatusTone =>
    tones[normalizeEnumKey(value)] ?? "neutral";

const formatDate = (value?: string | null) => formatLocalDate(value, "");

const formatDateRange = (arrival?: string | null, departure?: string | null) => {
    const parts = [formatDate(arrival), formatDate(departure)].filter(Boolean);
    return parts.length ? parts.join(" - ") : "-";
};

const buildRoomingCsvDownloadUrl = (eventId: number) =>
    `/api/events/${eventId}/accommodations/export/rooming/csv`;

const isPositiveInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value > 0;

const parseRouteEventId = (value?: string) => {
    const parsed = Number(value);
    return isPositiveInteger(parsed) ? parsed : undefined;
};

export const EventRoomingListPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const errorLabels = buildUserFacingErrorLabels(t);
    const {eventId} = useParams<{ eventId?: string }>();
    const numericEventId = useMemo(() => parseRouteEventId(eventId), [eventId]);
    const [page, setPage] = useState(1);
    const [downloadingRoomingCsv, setDownloadingRoomingCsv] = useState(false);
    const enumLabel = useCallback((value?: string | null) =>
        value ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value)}) : "-", [t]);

    useEffect(() => {
        setPage(1);
    }, [numericEventId]);

    const listVariables = useMemo(() => numericEventId
        ? {
            eventId: numericEventId,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE
        }
        : undefined, [numericEventId, page]);

    const roomingQuery = useEventRoomingListQuery({
        variables: listVariables,
        skip: !listVariables,
        fetchPolicy: "cache-and-network"
    });

    const [releaseReservation, releaseState] = useReleaseEventRoomReservationMutation();

    const registrations = useMemo(() => (roomingQuery.data?.eventRegistrations?.items ?? [])
        .filter((item): item is EventRoomingRegistration =>
            !!item
            && item.hasAccommodationReservation === true
            && isPositiveInteger(item.accommodationReservationId)), [roomingQuery.data?.eventRegistrations?.items]);

    const totalCount = roomingQuery.data?.eventRegistrations?.totalCount ?? 0;

    const handleReleaseReservation = useCallback(async (registration: EventRoomingRegistration) => {
        const reservationId = registration.accommodationReservationId;
        if (!reservationId) {
            return;
        }

        try {
            await releaseReservation({
                variables: {
                    id: reservationId
                }
            });
            await roomingQuery.refetch();
            message.success(t("room-reservation-released", {defaultValue: "Room reservation released."}));
        } catch (error) {
            message.error(t("room-reservation-release-failed", {defaultValue: "Could not release room reservation."}));
        }
    }, [message, releaseReservation, roomingQuery, t]);

    const handleDownloadRoomingCsv = useCallback(async () => {
        if (!numericEventId) {
            return;
        }

        setDownloadingRoomingCsv(true);
        try {
            const response = await fetch(
                buildRoomingCsvDownloadUrl(numericEventId),
                buildAuthorizedFileFetchOptions(auth.token)
            );
            if (!response.ok) {
                throw new Error(`Rooming CSV export failed with status ${response.status}`);
            }

            await downloadResponseFile(response, `event-${numericEventId}-rooming.csv`);
            message.success(t("rooming-csv-download-success", {
                defaultValue: "Rooming CSV downloaded."
            }));
        } catch {
            message.error(t("rooming-csv-download-error", {
                defaultValue: "Rooming CSV could not be downloaded. Please try again."
            }));
        } finally {
            setDownloadingRoomingCsv(false);
        }
    }, [message, auth.token, numericEventId, t]);

    const columns = useMemo<TableColumnsType<EventRoomingRegistration>>(() => [
        {
            key: "participant",
            title: t("participant", {defaultValue: "Participant"}),
            sorter: textSorter(registration => registration.participantFullName),
            render: (_, registration) => {
                const participantName = registration.participantFullName
                    || t("participant-name-unavailable", {
                        defaultValue: "Participant name unavailable"
                    });

                return <Typography.Text strong>{participantName}</Typography.Text>;
            }
        },
        {
            key: "hotel",
            title: t("hotel", {defaultValue: "Hotel"}),
            sorter: textSorter(registration => sortText(registration.accommodationHotelName)),
            render: (_, registration) => {
                if (!registration.hasAccommodationReservation || !registration.accommodationReservationId) {
                    return <Typography.Text type="secondary">-</Typography.Text>;
                }

                return (
                    <Typography.Text>{registration.accommodationHotelName || "-"}</Typography.Text>
                );
            }
        },
        {
            key: "roomType",
            title: t("room-type", {defaultValue: "Room type"}),
            sorter: textSorter(registration => registration.accommodationRoomTypeName),
            render: (_, registration) => {
                if (!registration.hasAccommodationReservation || !registration.accommodationReservationId) {
                    return <Typography.Text type="secondary">-</Typography.Text>;
                }

                return <Typography.Text>{registration.accommodationRoomTypeName || "-"}</Typography.Text>;
            }
        },
        {
            key: "stay",
            title: t("stay", {defaultValue: "Stay"}),
            sorter: dateSorter(registration => registration.accommodationArrivalDate),
            render: (_, registration) => {
                if (!registration.hasAccommodationReservation || !registration.accommodationReservationId) {
                    return <StatusTag>{t("no-reservation", {defaultValue: "None"})}</StatusTag>;
                }

                return (
                    <Space direction="vertical" size={2}>
                        <Typography.Text>{formatDateRange(
                            registration.accommodationArrivalDate,
                            registration.accommodationDepartureDate
                        )}</Typography.Text>
                        <StatusTag tone={tagTone(registration.accommodationReservationStatus, {
                            cancelled: "error",
                            released: "warning",
                            reserved: "success"
                        })}>
                            {enumLabel(registration.accommodationReservationStatus)}
                        </StatusTag>
                    </Space>
                );
            }
        },
        {
            key: "accommodationPayment",
            title: t("accommodation-payment-status", {defaultValue: "Accommodation payment status"}),
            sorter: textSorter(registration => registration.currentAccommodationOfferPaymentStatus),
            render: (_, registration) => {
                if (!registration.hasAccommodationReservation || !registration.accommodationReservationId) {
                    return <StatusTag>{t("no-reservation", {defaultValue: "None"})}</StatusTag>;
                }

                if (!registration.currentAccommodationOfferPaymentStatus) {
                    return <StatusTag>{t("accommodation-payment-not-issued", {defaultValue: "Unissued"})}</StatusTag>;
                }

                return (
                    <StatusTag tone={tagTone(registration.currentAccommodationOfferPaymentStatus, {
                        cancelled: "error",
                        failed: "error",
                        manuallyreconciled: "info",
                        paid: "success",
                        pending: "warning",
                        refunded: "neutral",
                        unpaid: "neutral"
                    })}>
                        {enumLabel(registration.currentAccommodationOfferPaymentStatus)}
                    </StatusTag>
                );
            }
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, registration) => {
                const canRelease = !!registration.accommodationReservationId
                    && normalizeEnumKey(registration.accommodationReservationStatus) === "reserved";

                return (
                    <Popconfirm
                        title={t("release-room-reservation-confirm-title", {defaultValue: "Release this room reservation?"})}
                        description={t("release-room-reservation-confirm-description", {
                            defaultValue: "Only the room reservation will be released. The participant and registration stay in the event."
                        })}
                        okText={t("release", {defaultValue: "Release"})}
                        cancelText={t("cancel", {defaultValue: "Cancel"})}
                        disabled={!canRelease}
                        onConfirm={() => handleReleaseReservation(registration)}
                    >
                        <Button
                            danger
                            disabled={!canRelease}
                            icon={<StopOutlined/>}
                            loading={releaseState.loading}
                        >
                            {t("release", {defaultValue: "Release"})}
                        </Button>
                    </Popconfirm>
                );
            }
        }
    ], [enumLabel, handleReleaseReservation, releaseState.loading, t]);

    return (
        <div className="ati-workspace-page">
            <PageHeader title={t("event-workspace.rooming", {defaultValue: "Reservations"})} level={2}/>
            <div className="ati-stack">
                {!numericEventId ? (
                    <Alert
                        type="info"
                        showIcon
                        message={t("select-event-to-view-rooming-list", {defaultValue: "Select an event to view rooming details."})}
                    />
                ) : null}

                {roomingQuery.error ? (
                    <Alert
                        type="error"
                        showIcon
                        message={t("error-loading-items", {defaultValue: "Error loading items"})}
                        description={getUserFacingErrorMessage(roomingQuery.error, t, errorLabels)}
                    />
                ) : null}

                <Card
                    size="small"
                    title={t("rooming-operations", {defaultValue: "Accommodation rooming"})}
                    extra={(
                        <Space size={8} wrap>
                            <Button
                                icon={<DownloadOutlined/>}
                                style={{marginBlock: 8}}
                                loading={downloadingRoomingCsv}
                                disabled={!numericEventId}
                                onClick={handleDownloadRoomingCsv}
                            >
                                {t("download-rooming-csv", {defaultValue: "Download rooming CSV"})}
                            </Button>
                        </Space>
                    )}
                >
                    <Table
                        size="small"
                        rowKey="id"
                        columns={columns}
                        dataSource={registrations}
                        loading={roomingQuery.loading || releaseState.loading}
                        pagination={{
                            current: page,
                            pageSize: PAGE_SIZE,
                            total: totalCount,
                            showSizeChanger: false,
                            onChange: nextPage => setPage(nextPage)
                        }}
                        locale={{
                            emptyText: numericEventId
                                ? t("no-rooming-registrations-found", {defaultValue: "No accommodation reservations found for this event."})
                                : t("select-event-to-view-rooming-list", {defaultValue: "Select an event to view rooming details."})
                        }}
                        scroll={{x: 920}}
                    />
                </Card>

            </div>
        </div>
    );
};

export default EventRoomingListPage;
